import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, MapPin, MessageCircle, HeartHandshake, ArrowRight, 
  Target, CheckCircle2, TrendingUp, Shield, BarChart3, 
  Building, Award, ChevronRight,
  IndianRupee, ClipboardCheck, Phone, Database, ChevronDown,
  Lightbulb, Heart, Rocket, Quote, Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';

const HomePage = () => {
  const [activeImpact, setActiveImpact] = useState(0);
  const [counters, setCounters] = useState({ villages: 0, women: 0, complaints: 0, funds: 0 });

  // Animated counter effect
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const targets = { villages: 352, women: 100000, complaints: 10000, funds: 95 };
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setCounters({
        villages: Math.floor(targets.villages * progress),
        women: Math.floor(targets.women * progress),
        complaints: Math.floor(targets.complaints * progress),
        funds: Math.floor(targets.funds * progress)
      });
      if (step >= steps) clearInterval(timer);
    }, duration / steps);

    return () => clearInterval(timer);
  }, []);

  // Auto-rotate impact stories (pauses when user interacts)
  const [isImpactPaused, setIsImpactPaused] = useState(false);
  
  useEffect(() => {
    if (isImpactPaused) return; // Don't auto-rotate when paused
    
    const timer = setInterval(() => {
      setActiveImpact(prev => (prev + 1) % 4);
    }, 10000); // Increased to 10 seconds
    return () => clearInterval(timer);
  }, [isImpactPaused]);

  // Handle impact card click - pause auto-rotation
  const handleImpactClick = (index) => {
    setActiveImpact(index);
    setIsImpactPaused(true); // Stop auto-switching when user clicks
  };

  const impactStories = [
    {
      category: "Administrative Excellence",
      title: "Government funds now reach exactly who needs them most",
      description: "Our digital tracking ensures 95%+ accuracy in beneficiary identification, eliminating leakages and building public trust.",
      metric: "₹2.5 Cr+",
      metricLabel: "Funds accurately distributed",
      icon: IndianRupee
    },
    {
      category: "Real-Time Intelligence",
      title: "Monthly surveys reveal what matters to your constituents",
      description: "From water and electricity to roads and healthcare — get a pulse on critical issues before they become crises.",
      metric: "85%",
      metricLabel: "Issue resolution rate",
      icon: BarChart3
    },
    {
      category: "Political Capital",
      title: "Every delivery creates a personal connection",
      description: "Our agents don't just deliver products — they become your trusted representatives in every household.",
      metric: "100K+",
      metricLabel: "Monthly touchpoints",
      icon: HeartHandshake
    },
    {
      category: "Women Empowerment",
      title: "Creating employment while building your network",
      description: "200+ women agents earning dignified incomes while extending your reach to the grassroots.",
      metric: "200+",
      metricLabel: "Women employed",
      icon: Users
    }
  ];

  const problemSolutions = [
    {
      problem: "Funds meant for welfare never reach beneficiaries",
      solution: "OTP-verified, digitally tracked distribution with real-time dashboards",
      icon: Shield
    },
    {
      problem: "No systematic way to understand constituency needs",
      solution: "Regular sentiment surveys integrated with every service delivery",
      icon: ClipboardCheck
    },
    {
      problem: "Complaints get lost, constituents feel ignored",
      solution: "Ticketing system with escalation protocols and resolution tracking",
      icon: MessageCircle
    },
    {
      problem: "No direct communication channel to households",
      solution: "Database of verified contacts built through trusted service delivery",
      icon: Database
    }
  ];

  const clientTypes = [
    { 
      title: "Independent Leaders", 
      description: "MLAs, MPs, and Zila Parishad members seeking to maximize their development impact",
      benefits: ["Build verified voter database", "Track scheme implementation", "Measure constituent satisfaction"]
    },
    { 
      title: "Government Organizations", 
      description: "District administrations and departments implementing welfare programs",
      benefits: ["Ensure last-mile delivery", "Real-time monitoring", "Transparent reporting"]
    }
  ];

  const projectHighlights = [
    {
      name: "Majhi Mauli Smile",
      logo: "https://customer-assets-wrfwihn1.emergentagent.net/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/qfc3xy6x_Logo%20Project%20Majhi%20Mauli%20Smile%202.png",
      product: "Sanitary Pad Distribution",
      constituencies: ["Ramtek", "Saoner"],
      description: "A women-centric welfare initiative delivering sanitary pads directly to households across two constituencies, empowering women's health and hygiene while creating a trusted connection between beneficiaries and their representatives.",
      stats: [
        { label: "Women Agents", value: "200+" },
        { label: "Monthly Deliveries", value: "100K" },
        { label: "Resolution Rate", value: "85%" }
      ],
      achievements: [
        "100% digital tracking of all sanitary pad distributions",
        "Monthly sentiment surveys across 352+ gram panchayats",
        "Women-led agent network creating local employment",
        "Direct beneficiary database of 100,000+ households"
      ]
    }
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
        <div className="absolute top-20 left-20 w-32 h-32 border-l-2 border-t-2 border-cyan-500/20 rounded-tl-3xl" />
        <div className="absolute bottom-20 right-20 w-32 h-32 border-r-2 border-b-2 border-cyan-500/20 rounded-br-3xl" />
        
        <div className="relative z-10 min-h-screen flex flex-col">
          <div className="flex-1 flex items-center justify-center px-6 pt-24 pb-12">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-flex items-center gap-3 mb-10 animate-fade-in-up">
                <div className="h-px w-8 bg-gradient-to-r from-transparent to-cyan-500" />
                <span className="text-cyan-400 text-sm font-medium tracking-wider uppercase">
                  Technology for Governance
                </span>
                <div className="h-px w-8 bg-gradient-to-l from-transparent to-cyan-500" />
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold mb-8 leading-[1.1] tracking-tight animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                <span className="text-white">Transforming</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                  Public Welfare Delivery
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                End-to-end IT solutions enabling transparent, accountable, and measurable 
                governance outcomes for public representatives and administrative bodies.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <Button 
                  size="lg" 
                  className="group bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-8 py-6 text-base rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25"
                  asChild
                >
                  <Link to="/contact">
                    Request Consultation
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="group border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-slate-600 px-8 py-6 text-base rounded-lg transition-all duration-300"
                  asChild
                >
                  <Link to="/products/pds">
                    View Solutions
                  </Link>
                </Button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-800/50 rounded-xl overflow-hidden max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                {[
                  { value: counters.villages, suffix: '+', label: 'Gram Panchayats' },
                  { value: counters.women >= 1000 ? `${Math.floor(counters.women/1000)}K` : counters.women, suffix: '+', label: 'Beneficiaries Monthly' },
                  { value: counters.complaints >= 1000 ? `${Math.floor(counters.complaints/1000)}K` : counters.complaints, suffix: '+', label: 'Issues Resolved' },
                  { value: counters.funds, suffix: '%', label: 'Fund Accuracy' }
                ].map((stat, i) => (
                  <div key={i} className="bg-slate-900/80 backdrop-blur-sm p-6 text-center">
                    <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                      {stat.value}{stat.suffix}
                    </div>
                    <div className="text-xs text-slate-500 uppercase tracking-wide">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="pb-8 flex justify-center animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            <button 
              onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
              className="flex flex-col items-center gap-2 text-slate-500 hover:text-cyan-400 transition-colors group"
            >
              <span className="text-xs uppercase tracking-widest">Learn More</span>
              <ChevronDown className="w-5 h-5 animate-bounce" />
            </button>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-6 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4 text-slate-400 text-sm">
            <span className="font-medium text-slate-600">Trusted by leaders across Central India</span>
            <div className="hidden sm:block h-4 w-px bg-slate-200" />
            <span>Since 2021</span>
            <div className="hidden sm:block h-4 w-px bg-slate-200" />
            <span>352+ Gram Panchayats</span>
            <div className="hidden sm:block h-4 w-px bg-slate-200" />
            <span>85% Resolution Rate</span>
          </div>
        </div>
      </section>

      {/* Our Philosophy / Why We Do This */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-50 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 opacity-50" />
        
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Philosophy */}
            <div>
              <div className="inline-flex items-center gap-2 text-cyan-600 text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                <span className="uppercase tracking-wider">Our Philosophy</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                When Families Get Real Benefits, <span className="text-cyan-600">Trust Increases Manifold</span>
              </h2>
              
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                This is the safest investment a public servant can make — in the trust of their constituents. We've built AEPL on a simple belief: technology should make governance more human, not less.
              </p>
              
              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm mb-8">
                <div className="flex items-start gap-4">
                  <Quote className="w-8 h-8 text-cyan-500 flex-shrink-0" />
                  <div>
                    <p className="text-slate-700 italic mb-3">
                      "Every interaction between a beneficiary and your representative builds political capital that no advertisement can buy."
                    </p>
                    <p className="text-sm text-slate-500">— The AEPL Philosophy</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  { icon: Target, title: "Data-Driven Governance", desc: "Every decision backed by real constituent data" },
                  { icon: Heart, title: "Human-First Technology", desc: "Systems designed to serve people, not replace them" },
                  { icon: Rocket, title: "Measurable Impact", desc: "Clear metrics for every initiative we deploy" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{item.title}</h4>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Visual representation */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {/* Top Left - Big Stat */}
                <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl p-6 text-white col-span-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-5xl font-bold mb-2">100,000+</div>
                      <div className="text-cyan-100">Women receive dignified service every month</div>
                    </div>
                    <HeartHandshake className="w-16 h-16 text-white/30" />
                  </div>
                </div>
                
                {/* Bottom Cards */}
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-lg">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900 mb-1">95%</div>
                  <div className="text-sm text-slate-500">Fund Accuracy Rate</div>
                </div>
                
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-lg">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900 mb-1">200+</div>
                  <div className="text-sm text-slate-500">Women Employed</div>
                </div>
                
                {/* Trust Banner */}
                <div className="col-span-2 bg-slate-900 rounded-2xl p-5 text-white">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <div className="font-semibold">Trust is the Currency</div>
                      <div className="text-sm text-slate-400">Every delivery builds a connection no campaign can replicate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Showcase - Dedicated Section */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-cyan-400 text-sm font-medium mb-4">
              <Smartphone className="w-4 h-4" />
              <span className="uppercase tracking-wider">Our Technology</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Gramtek <span className="text-cyan-400">Mobile Platform</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Designed for simplicity. Built for rural India. Accessible to everyone.
            </p>
          </div>

          {/* Large App Mockup Display */}
          <div className="relative max-w-5xl mx-auto mb-12">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-3xl blur-3xl" />
            <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-4 md:p-8 border border-slate-700">
              <img 
                src="https://customer-assets.emergentagent.com/job_content-hub-780/artifacts/nitbdgm3_Shots%20Mockups%20%283%29.png"
                alt="Gramtek Mobile App - Agent Dashboard showing delivery tracking, beneficiary management, OTP verification, and real-time sync capabilities"
                className="w-full rounded-xl shadow-2xl"
              />
            </div>
          </div>

          {/* Feature Grid Below Mockup */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { title: "OTP Verification", desc: "Secure, verified deliveries", icon: Shield },
              { title: "Real-time Sync", desc: "Instant dashboard updates", icon: TrendingUp },
              { title: "Offline Capable", desc: "Works without internet", icon: Database },
              { title: "Multi-language", desc: "Regional language support", icon: MessageCircle }
            ].map((feature, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 text-center hover:bg-white/10 transition-colors group">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                <p className="text-xs text-slate-400">{feature.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-slate-400 text-sm mb-4">
              Designed for women agents with minimal smartphone experience. Large buttons, clear icons, intuitive workflow.
            </p>
            <Button 
              size="lg"
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold"
              asChild
            >
              <Link to="/products/pds">
                Learn More About Gramtek-PDS
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Project Spotlight */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-cyan-600 text-sm font-medium mb-4">
              <MapPin className="w-4 h-4" />
              <span className="uppercase tracking-wider">Active Projects</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Project <span className="text-cyan-600">Spotlight</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our on-ground initiatives transforming governance outcomes across constituencies
            </p>
          </div>

          {projectHighlights.map((project, i) => (
            <div key={i} className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 md:p-12 text-white mb-8 last:mb-0">
              {/* Project Header with Logo */}
              <div className="flex flex-col sm:flex-row items-center gap-6 mb-10 pb-8 border-b border-white/10">
                <img
                  src={project.logo}
                  alt={project.name}
                  className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-contain bg-white p-2"
                />
                <div className="text-center sm:text-left">
                  <h3 className="text-2xl md:text-3xl font-bold text-white">{project.name}</h3>
                  <p className="text-cyan-400 font-medium mt-1">{project.product}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {project.constituencies.map((c, j) => (
                      <span key={j} className="inline-flex items-center gap-1.5 bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full text-sm font-medium">
                        <MapPin className="w-3 h-3" />
                        {c} Constituency
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div>
                  <p className="text-slate-300 mb-8 leading-relaxed">{project.description}</p>
                  
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {project.stats.map((stat, j) => (
                      <div key={j} className="bg-white/5 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-cyan-400">{stat.value}</div>
                        <div className="text-xs text-slate-400">{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-white">Key Achievements:</h4>
                    <div className="space-y-2">
                      {project.achievements.map((item, j) => (
                        <div key={j} className="flex items-center gap-2 text-sm text-slate-300">
                          <CheckCircle2 className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link 
                    to="/majhi-mauli-smile" 
                    className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-6 py-3 rounded-lg transition-all duration-300 mt-6"
                    data-testid="know-more-mms"
                  >
                    Know More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="relative">
                  <div className="bg-slate-700/50 rounded-2xl p-6">
                    <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-amber-400" />
                      What Drives Us
                    </h4>
                    <div className="space-y-4">
                      <div className="bg-slate-600/30 rounded-xl p-4">
                        <h5 className="font-medium text-cyan-400 mb-2">The Vision</h5>
                        <p className="text-sm text-slate-300">To create a world where every public welfare scheme reaches its intended beneficiary with complete transparency.</p>
                      </div>
                      <div className="bg-slate-600/30 rounded-xl p-4">
                        <h5 className="font-medium text-cyan-400 mb-2">The Method</h5>
                        <p className="text-sm text-slate-300">Combine technology with human touch — our women agents are trusted members of the community, not just delivery personnel.</p>
                      </div>
                      <div className="bg-slate-600/30 rounded-xl p-4">
                        <h5 className="font-medium text-cyan-400 mb-2">The Result</h5>
                        <p className="text-sm text-slate-300">When families receive real benefits through a respectful, dignified process, their trust in governance multiplies exponentially.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* The Problem We Solve */}
      <section className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-50 rounded-full blur-3xl -translate-y-1/2 opacity-50" />
        
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <span className="w-2 h-2 bg-red-500 rounded-full" />
              The Challenge
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Public Welfare Has a <span className="text-red-500">Last-Mile Problem</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Despite good intentions, most welfare initiatives fail to create the impact they should.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {problemSolutions.map((item, i) => (
              <div 
                key={i} 
                className="group relative bg-white p-8 rounded-2xl border border-slate-100 hover:border-cyan-200 hover:shadow-xl transition-all duration-500"
              >
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-red-500 font-medium text-sm">Problem:</span>
                    </div>
                    <p className="text-slate-700 mb-4 font-medium">{item.problem}</p>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-500" />
                      <span className="text-cyan-600 font-medium text-sm">Our Solution:</span>
                    </div>
                    <p className="text-slate-600 text-sm">{item.solution}</p>
                  </div>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-b-2xl" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stories */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-cyan-400 text-sm font-medium mb-4">
              <TrendingUp className="w-4 h-4" />
              <span className="uppercase tracking-wider">Real Impact</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              What Technology-Enabled <span className="text-cyan-400">Governance</span> Looks Like
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Measurable outcomes from our implementations across rural India
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-2 space-y-3">
              {impactStories.map((story, i) => (
                <button
                  key={i}
                  onClick={() => handleImpactClick(i)}
                  className={`w-full text-left p-5 rounded-xl transition-all duration-300 ${
                    activeImpact === i 
                      ? 'bg-slate-800 border border-cyan-500/50' 
                      : 'bg-slate-800/50 border border-slate-700 hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                      activeImpact === i ? 'bg-cyan-500' : 'bg-slate-700'
                    }`}>
                      <story.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-cyan-400 font-medium mb-0.5">{story.category}</div>
                      <div className={`text-sm font-medium truncate transition-colors ${activeImpact === i ? 'text-white' : 'text-slate-400'}`}>
                        {story.title.substring(0, 35)}...
                      </div>
                    </div>
                    <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-all ${activeImpact === i ? 'text-cyan-400 translate-x-1' : 'text-slate-600'}`} />
                  </div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-3">
              <div className="relative bg-slate-800 rounded-2xl p-8 border border-slate-700 overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-cyan-500/5 rounded-full blur-2xl" />
                
                <div className="relative">
                  <div className="inline-block text-cyan-400 text-xs font-medium uppercase tracking-wider mb-3">
                    {impactStories[activeImpact].category}
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-4 text-white">
                    {impactStories[activeImpact].title}
                  </h3>
                  
                  <p className="text-slate-400 mb-8 leading-relaxed">
                    {impactStories[activeImpact].description}
                  </p>

                  <div className="flex items-end gap-6">
                    <div>
                      <div className="text-4xl font-bold text-cyan-400 mb-1">
                        {impactStories[activeImpact].metric}
                      </div>
                      <div className="text-xs text-slate-500 uppercase tracking-wide">{impactStories[activeImpact].metricLabel}</div>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-700">
                  <div 
                    className="h-full bg-cyan-500 transition-all duration-300"
                    style={{ width: `${((activeImpact + 1) / impactStories.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-cyan-600 text-sm font-medium mb-4">
              <Users className="w-4 h-4" />
              <span className="uppercase tracking-wider">Who We Serve</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Partnering with <span className="text-cyan-600">Decision Makers</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {clientTypes.map((client, i) => (
              <div 
                key={i}
                className="group relative bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl border border-slate-100 hover:border-cyan-200 transition-all duration-500"
              >
                <div className={`w-14 h-14 rounded-xl mb-6 flex items-center justify-center ${
                  i === 0 ? 'bg-gradient-to-br from-cyan-500 to-blue-600' : 'bg-gradient-to-br from-slate-700 to-slate-800'
                } group-hover:scale-110 transition-transform duration-300`}>
                  {i === 0 ? <Award className="w-7 h-7 text-white" /> : <Building className="w-7 h-7 text-white" />}
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-2">{client.title}</h3>
                <p className="text-slate-600 text-sm mb-6">{client.description}</p>
                
                <div className="space-y-2">
                  {client.benefits.map((benefit, j) => (
                    <div key={j} className="flex items-center gap-2 text-slate-600 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Solutions */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-cyan-600 text-sm font-medium mb-4">
              <Target className="w-4 h-4" />
              <span className="uppercase tracking-wider">Our Solutions</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Technology That <span className="text-cyan-600">Delivers</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Purpose-built solutions for measurable governance outcomes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Gramtek-PDS',
                subtitle: 'Public Distribution System',
                description: 'Women-led, door-to-door delivery with digital tracking and voter database building.',
                features: ['OTP-verified delivery', 'Real-time tracking', 'Voter database'],
                link: '/products/pds',
                color: 'from-cyan-500 to-blue-600',
                icon: HeartHandshake
              },
              {
                title: 'CRS',
                subtitle: 'Complaint Resolution',
                description: 'Systematic complaint management with escalation protocols and analytics.',
                features: ['Ticket management', 'Escalation protocols', 'Resolution analytics'],
                link: '/products/crs',
                color: 'from-amber-500 to-orange-600',
                icon: MessageCircle
              },
              {
                title: 'SRM',
                subtitle: 'Self-Reliance Mission',
                description: 'Holistic village transformation through sustainable development initiatives.',
                features: ['Community mobilization', 'Resource optimization', 'Impact measurement'],
                link: '/products/kachurwahi',
                color: 'from-emerald-500 to-teal-600',
                icon: Target
              }
            ].map((solution, i) => (
              <Link
                key={i}
                to={solution.link}
                className="group relative bg-white rounded-xl border border-slate-200 hover:border-cyan-200 hover:shadow-xl transition-all duration-500 overflow-hidden"
              >
                <div className="p-6">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${solution.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    <solution.icon className="w-6 h-6 text-white" />
                  </div>
                  
                  <div className="text-xs text-cyan-600 font-medium uppercase tracking-wide mb-1">{solution.subtitle}</div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-cyan-600 transition-colors">{solution.title}</h3>
                  <p className="text-slate-600 text-sm mb-5">{solution.description}</p>
                  
                  <div className="space-y-1.5 mb-5">
                    {solution.features.map((feature, j) => (
                      <div key={j} className="flex items-center gap-2 text-xs text-slate-500">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyan-500" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center text-cyan-600 text-sm font-semibold group-hover:gap-2 transition-all">
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
                
                <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${solution.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform <span className="text-cyan-400">Governance Outcomes</span>?
          </h2>
          
          <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
            Schedule a consultation to discuss implementation in your constituency or department.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-8 py-6 text-base rounded-lg transition-all duration-300"
              asChild
            >
              <Link to="/contact">
                Request Consultation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-6 text-base rounded-lg transition-all duration-300"
              asChild
            >
              <Link to="/products/pds#calculator">
                Investment Calculator
              </Link>
            </Button>
          </div>
          
          <p className="mt-6 text-slate-500 text-sm">
            30-minute consultation • Custom proposal within 48 hours
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
